import t from "babel-plugin-yate/macro";
import cuid from "cuid";
import { removeSpinner, addSpinner, addAlert } from "../../store/app";
import { logOut } from "../../store/auth";
import isArrayBuffer from "lodash-es/isArrayBuffer";
import isArray from "lodash-es/isArray";
import { getCookie } from "../cookieHelper";

const cacheRequest = new Set();

export const extractErrorMessage = (response) => {
  const messages =
    response?.data?.messages ||
    response?.data?.error_message ||
    response?.data?.error ||
    response?.problem ||
    response?.messages;

  if (isArray(messages)) {
    let errorMessage = "";
    for (let errorMessageKey in messages) {
      errorMessage += `${messages[errorMessageKey]}\n`;
    }
    return errorMessage;
  }

  if (messages) {
    return messages;
  }

  return `${t`An unknown error has occured`}.`;
};

export const handleApiError = (params) => {
  const { response, dispatch, failAction, spinnerId, alert, alertMessage } =
    params;

  if (typeof failAction === "string") {
    dispatch({
      type: failAction,
      error: alertMessage ?? extractErrorMessage(response),
    });
  } else {
    dispatch({
      ...failAction,
      error: alertMessage ?? extractErrorMessage(response),
    });
  }

  spinnerId && dispatch(removeSpinner(spinnerId));
  alert &&
    dispatch(
      addAlert({
        message: alertMessage ?? extractErrorMessage(response),
        variant: "error",
      }),
    );

  return {
    error: true,
    response,
    errorMessage: alertMessage ?? extractErrorMessage(response),
  };
};

export const checkApiSuccess = (response) => {
  return response.ok && !response.problem && !response.data?.error;
};

export const makeApiCall = async (actionParams) => {
  const {
    action,
    dispatch,
    apiCall,
    apiArgs = [],
    spinner = false,
    spinnerMessage = null,
    alert = true,
    alertMessage = null,
    formatResponse,
    returnResponse = true,
    preAction,
    postAction,
    preSuccess,
    postSuccess,
    preFail,
    postFail,
  } = actionParams;

  const headers = {};
  const actions = {
    action: action,
    success: `${action}_SUCCESS`,
    fail: `${action}_FAILURE`,
  };

  const hashApiCall = `${action}/${JSON.stringify(apiArgs)}`;

  if (cacheRequest.has(hashApiCall)) {
    return;
  }

  cacheRequest.add(hashApiCall);

  const token = getCookie("userToken");

  /**
   * avoid setting the 'X-User-Token' HTTP header
   * for the endpoints which are using '/v2_0/' route
   * */
  if (token) {
    headers["X-User-Token"] = token;
  }

  const actionArgs = [...apiArgs];

  apiArgs.push(headers);

  const spinnerId = cuid();

  if (preAction) {
    await preAction();
  }

  if (typeof actions.action === "string") {
    dispatch({
      type: actions.action,
    });
  } else {
    dispatch(actions.action);
  }

  if (spinner) {
    dispatch(addSpinner(spinnerId, spinnerMessage));
  }

  if (postAction) {
    await postAction();
  }

  return apiCall(...apiArgs).then(async (response) => {
    if (checkApiSuccess(response)) {
      preSuccess && (await preSuccess(response));

      const res = formatResponse ? formatResponse(response) : response;

      if (typeof actions.success === "string") {
        dispatch({
          type: actions.success,
          payload: res,
          args: actionArgs,
        });
      } else {
        dispatch({
          ...actions.success,
          payload: res,
          args: actionArgs,
        });
      }

      postSuccess && (await postSuccess(response));
      spinner && dispatch(removeSpinner(spinnerId));
      cacheRequest.delete(hashApiCall);

      if (returnResponse) {
        return res;
      }
    } else {
      let errorResponse = response;

      if (isArrayBuffer(response.data)) {
        const newUintArray = new Uint8Array(response.data);
        errorResponse = JSON.parse(new TextDecoder().decode(newUintArray));
      }

      preFail && (await preFail(errorResponse));

      const err = handleApiError({
        response: errorResponse,
        dispatch,
        failAction: actions.fail,
        spinnerId: spinner ? spinnerId : false,
        alert,
        alertMessage,
      });

      postFail && (await postFail(response));
      cacheRequest.delete(hashApiCall);

      if (response.status === 401 || response.status === 403) {
        await dispatch(logOut());
      }

      if (returnResponse) {
        return err;
      }
    }
  });
};
